import Head from 'next/head'
import { useRouter } from 'next/router'
import React from 'react'

import {
  HeaderBar,
  HeaderBarLeftAction,
  HeaderBarTitle,
} from '@wartek-id/header-bar'
import { Icon } from '@wartek-id/icon'
import { historyUtils } from '@wartek-id/toolbox-helper'

import { useGetPlaylistById } from 'api/video-repository/useAPIPlaylists'
import {
  VideoDescriptionShimmer,
  VideoCardShimmer,
  VideoCountShimmer,
} from 'app/VideoRepository/components/Preloader'
import Playlist from 'app/VideoRepository/Playlist'
import DesktopPlaylistSplitView from 'app/VideoRepository/components/DesktopPlaylistSplitView'
import { useVideoInspirasiDesktopView } from 'app/VideoRepository/hooks/useVideoInspirasiDesktopView'
import { DesktopPageHeader } from 'components/ResponsiveLayout'
import { EmptyState, EmptyStateAction } from 'components/EmptyState'

const TRACKER_SCREEN_NAME = 'video_inspirasi_playlist_detail'
const HELPDESK_LINK = 'https://pusatinformasi.guru.kemdikbud.go.id'

const VideoRepositoryById = () => {
  const { query } = useRouter()
  const { data, isSuccess, isError, isLoading } = useGetPlaylistById(
    query.id as string
  )
  const containerRef = React.useRef<HTMLDivElement>(null)
  const router = useRouter()
  const { isDesktop } = useVideoInspirasiDesktopView()

  if (isDesktop) {
    const playlistData = data?.data

    return (
      <>
        <Head>
          <title>Video Inspirasi | {playlistData?.name ?? 'Playlists'} </title>
        </Head>
        <div className="flex flex-col h-screen">
          <DesktopPageHeader
            navLinks={[
              { label: 'Bantuan', href: HELPDESK_LINK, external: true },
            ]}
            breadcrumbItems={[
              { label: 'Ide Praktik', href: '/video-inspirasi' },
              { label: playlistData?.name ?? 'Playlists', href: null },
            ]}
          />
          <DesktopPlaylistSplitView
            playlistId={playlistData?.id ?? (query.id as string)}
            playlistName={playlistData?.name ?? ''}
            shortDescription={playlistData?.shortDescription ?? ''}
            longDescription={playlistData?.longDescription ?? ''}
            videos={playlistData?.videos ?? []}
            reviewer={playlistData?.reviewer}
            isLoading={isLoading}
            isSuccess={isSuccess}
            screenName={TRACKER_SCREEN_NAME}
            initialVideoId={query.video as string}
          />
          {isError && (
            <EmptyState
              title={'Gagal mengambil data'}
              description={'Data Repository tidak dapat diakses'}
            >
              <EmptyStateAction onClick={() => window.location.reload()}>
                Muat Ulang
              </EmptyStateAction>
            </EmptyState>
          )}
        </div>
      </>
    )
  }

  return (
    <>
      <Head>
        <title>Video Inspirasi | {data?.data?.name ?? 'Playlists'} </title>
      </Head>
      <HeaderBar fixed borderless variant="large">
        <HeaderBarLeftAction
          icon={<Icon as="i">arrow_back</Icon>}
          onClick={() => historyUtils.backToApp(router.back)}
        />
        <HeaderBarTitle>{data?.data?.name}</HeaderBarTitle>
      </HeaderBar>
      <div
        ref={containerRef}
        className="bg-default max-w-xl mx-auto"
        style={{
          minHeight: 'calc(100vh - 3.5rem)',
        }}
      >
        {isLoading && (
          <>
            <div
              className="p-4 bg-white"
              style={{
                boxShadow:
                  '0 2px 1px rgba(0, 0, 0, .05), 0 0 1px rgba(0, 0, 0, .25)',
              }}
            >
              <VideoDescriptionShimmer />
            </div>
            <div className="p-4">
              <VideoCountShimmer />
              {[...Array(4)].map((_, index) => (
                <VideoCardShimmer key={index} />
              ))}
            </div>
          </>
        )}
        {isSuccess && (
          <Playlist
            containerRef={containerRef}
            name={data?.data?.name}
            playlistId={data?.data?.id}
            shortDescription={data?.data?.shortDescription}
            videos={data?.data?.videos}
            videoCount={data?.data?.videos?.length ?? ''}
            longDescription={data?.data?.longDescription}
            reviewer={data?.data?.reviewer}
            screenName={TRACKER_SCREEN_NAME}
          />
        )}
        {isError && (
          <EmptyState
            title={'Gagal mengambil data'}
            description={'Data Repository tidak dapat diakses'}
          >
            <EmptyStateAction onClick={() => window.location.reload()}>
              Muat Ulang
            </EmptyStateAction>
          </EmptyState>
        )}
      </div>
    </>
  )
}

VideoRepositoryById.layoutOpts = {
  className: 'pt-[94px]',
}

VideoRepositoryById.Layout = ({ children }) => children

export default VideoRepositoryById
